/**
 * useEventStream — Server-Sent Events hook.
 *
 * Connects to /api/events/stream with the JWT token as a query param
 * (EventSource doesn't support custom headers). Auto-reconnects on disconnect.
 *
 * Usage:
 *   useEventStream({
 *     onEvent: (event) => { ... },
 *     types: ["dossier:started", "dossier:completed"], // optional filter
 *   });
 */
import { useEffect, useRef, useState } from "react";
import { API } from "./api";

export function useEventStream({ onEvent, types } = {}) {
  const [connected, setConnected] = useState(false);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    const token = localStorage.getItem("norea_token");
    if (!token) return;

    let es = null;
    let reconnectTimer = null;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      es = new EventSource(`${API}/events/stream?token=${encodeURIComponent(token)}`);

      es.onopen = () => setConnected(true);
      es.onerror = () => {
        setConnected(false);
        es?.close();
        if (!stopped) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      };

      // Generic message handler
      const handler = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (types && !types.includes(data.type)) return;
          onEventRef.current?.(data);
        } catch (err) {
          // ignore parse errors
        }
      };

      // Listen to all known event types
      const knownTypes = [
        "hello",
        "dossier:started",
        "dossier:step",
        "dossier:completed",
        "dossier:status",
        "gmail:poll",
        "dossier:test",
      ];
      knownTypes.forEach((t) => es.addEventListener(t, handler));
      es.onmessage = handler; // fallback
    };

    connect();

    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      es?.close();
    };
  }, [types?.join(",")]);

  return { connected };
}
